import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { isAnyMediaPlayingAtom } from './atoms/media-atoms';
type UseAutoPauseDueToInactivityArgsT = {
  timeoutMinutes: number | null;
  onPause: () => void;
  onWarning?: () => void;
  onDismissWarning?: () => void;
  onPageUnload: () => void;
  onBeforeUnload: () => void;
  enabled?: boolean;
};
const WARNING_LEAD_TIME_SECONDS = 10;
const MIN_TIMEOUT_MINUTES = WARNING_LEAD_TIME_SECONDS / 60;
const registerActivityListeners = (events: string[], handler: () => void) => {
  events.forEach(event => {
    document.addEventListener(event, handler, {
      passive: true
    });
  });
  document.addEventListener('auto-pause:activity', handler);
  return () => {
    events.forEach(event => {
      document.removeEventListener(event, handler);
    });
    document.removeEventListener('auto-pause:activity', handler);
  };
};
const getCurrentData = () => Date.now();
export function useAutoPauseDueToInactivity({
  timeoutMinutes,
  onPause,
  onWarning,
  onDismissWarning,
  onPageUnload,
  onBeforeUnload,
  enabled = false
}: UseAutoPauseDueToInactivityArgsT) {
  const isMediaPlaying = useAtomValue(isAnyMediaPlayingAtom);
  const lastActivityRef = useRef<number>(getCurrentData());
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const pauseTriggeredRef = useRef<boolean>(false);
  const warningTriggeredRef = useRef<boolean>(false);
  const safeTimeoutMinutes = useMemo(() => {
    if (timeoutMinutes === null || timeoutMinutes <= 0) {
      return null;
    }
    return Math.max(timeoutMinutes, MIN_TIMEOUT_MINUTES);
  }, [timeoutMinutes]);
  const resetActivityTimer = useCallback(() => {
    const wasWarningTriggered = warningTriggeredRef.current;
    const isPauseInProgress = pauseTriggeredRef.current;
    lastActivityRef.current = Date.now();
    pauseTriggeredRef.current = false;
    warningTriggeredRef.current = false;

    // Only dismiss warning toast if pause is not in progress
    if (wasWarningTriggered && !isPauseInProgress && onDismissWarning) {
      onDismissWarning();
    }
  }, [onDismissWarning]);
  const checkInactivity = useCallback(() => {
    if (!enabled || !safeTimeoutMinutes || pauseTriggeredRef.current) {
      return;
    }
    const now = Date.now();
    const inactiveMs = now - lastActivityRef.current;
    const timeoutMs = safeTimeoutMinutes * 60 * 1000;
    const warningThresholdMs = timeoutMs - WARNING_LEAD_TIME_SECONDS * 1000;
    if (isMediaPlaying) {
      resetActivityTimer();
      return;
    }
    if (inactiveMs >= timeoutMs) {
      pauseTriggeredRef.current = true;
      onPause();
    } else if (inactiveMs >= warningThresholdMs && !warningTriggeredRef.current && onWarning) {
      warningTriggeredRef.current = true;
      onWarning();
    }
  }, [enabled, safeTimeoutMinutes, isMediaPlaying, onPause, onWarning, resetActivityTimer]);
  const isInitializedRef = useRef<boolean>(false);
  useEffect(() => {
    if (!enabled || !safeTimeoutMinutes) {
      return;
    }
    if (!isInitializedRef.current) {
      resetActivityTimer();
      isInitializedRef.current = true;
    }
  }, [enabled, safeTimeoutMinutes, resetActivityTimer]);
  useEffect(() => {
    if (!enabled || !safeTimeoutMinutes) {
      return;
    }
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'keydown', 'scroll', 'touchstart', 'touchmove', 'click', 'wheel', 'focus', 'focusin', 'input'];
    const handleActivity = () => {
      resetActivityTimer();
    };
    const removeListeners = registerActivityListeners(activityEvents, handleActivity);
    const checkIntervalMs = 1 * 1000; // Check every 1 second
    timerRef.current = setInterval(() => {
      checkInactivity();
    }, checkIntervalMs);
    return () => {
      removeListeners();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [enabled, safeTimeoutMinutes, resetActivityTimer, checkInactivity]);
  useEffect(() => {
    if (!enabled || !safeTimeoutMinutes) {
      return;
    }
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        resetActivityTimer();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, safeTimeoutMinutes, resetActivityTimer]);
  useEffect(() => {
    if (isMediaPlaying) {
      resetActivityTimer();
    }
  }, [isMediaPlaying, resetActivityTimer]);
  useEffect(() => {
    if (!enabled || !safeTimeoutMinutes) {
      return;
    }
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (pauseTriggeredRef.current) return;
      onBeforeUnload();
      e.preventDefault();
    };
    const handlePageHide = (e: PageTransitionEvent) => {
      if (e.persisted) return;
      if (pauseTriggeredRef.current) return;
      onPageUnload();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [enabled, safeTimeoutMinutes, onPageUnload, onBeforeUnload]);
  return {
    resetTimer: resetActivityTimer
  };
}